<template>
  <div>
    <div class="text-center w-full">
      <p class="text-24 md:text-47 font-bold">You received a code</p>
      <p class="text-20 text-grey-20 md:-mx-15">Please check your email for a 6-digit code and enter it below.</p>
    </div>

    <InputModule
      v-model="confirmCode"
      type="number"
      name="confirmCode"
      label="Enter code"
      placeholder="352846"
      class="w-full mt-30 font-bold"
      input-classes="px-22 py-13 rounded-10 border border-grey-10 focus:border-brand anim-all text-17"
      label-class="mb-5 font-bold"
      shadow="focus:shadow-signup"
      max-length="6"
      :has-background="false"
      @input="maxLengthCount"
    />

    <div class="text-14 w-full">
      <div>
        <ButtonModule
          text="Click here"
          class="text-brand mt-10"
          :disabled="isDisabledCodeButton"
          :class="isDisabledCodeButton ? 'opacity-50' : ''"
          @click="sendVerificationCode"
        />

        <span>to receive a new code</span>
      </div>
    </div>

    <ButtonModule
      text="Continue"
      :disabled="confirmCode.length !== 6"
      :class="confirmCode.length === 6 ? 'bg-brand hover:bg-brand-hover' : 'bg-grey-750'"
      suffix="relative icon-arrow-right ml-10 mt-2 text-10 left-0 group-hover:left-7 anim-all"
      class="w-full justify-center bg-brand hover:bg-brand-hover text-white rounded-10 mt-20 py-16 group font-medium text-17"
      @click="checkConfirmCode"
    />

    <p class="text-grey-20 text-center mt-20 px-30">Keep this window open while getting your code, and don’t forget to check your spam folder.</p>

    <SignupLoading
      v-if="showLastStep"
      :is-mockup="isMockupLoading"
      :title="loadingTitle"
      :sub-title="loadingSubTitle"
      :initial-value="51"
    />
  </div>
</template>

<script>
import { defineComponent, ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { useGtag } from 'vue-gtag-next'

import { teamsService, settingsService } from '@/services'

import InputModule from '@/components/shared/form-controls/InputModule'
import ButtonModule from '@/components/shared/form-controls/ButtonModule'
import SignupLoading from '@/components/auth/SignupLoading'

import { firstTeamBook } from '@/onboarding'

export default defineComponent({
  name: 'SignUpTeams',

  components: { InputModule, ButtonModule, SignupLoading },

  props: {
    user: Object,
    team: Object,
    hasAccounts: Object,
    hasInvite: Boolean
  },

  emits: ['next'],

  setup (props, { emit }) {
    const { state, commit, dispatch } = useStore()
    const route = useRoute()
    const router = useRouter()
    const { event } = useGtag()

    const confirmCode = ref('')
    const isDisabledCodeButton = ref(false)
    const teamId = ref(null)
    const signature = ref(null)
    const expire = ref(null)

    const showLastStep = ref(false)
    const isMockupLoading = ref(false)
    const loadingTitle = ref('You’re all set!')
    const loadingSubTitle = ref('We’re preparing your workspace.')

    const settings = computed(() => state.settings.settings)

    const url = computed(() => {
      return route.query ? { name: 'Login', query: route.query } : { name: 'Login' }
    })

    onMounted(() => {
      teamsService.sendVerificationCode({ email: props.user.email, teamName: props.team.name })
      if (props.hasInvite) {
        teamId.value = route.query.teamId
        signature.value = route.query.signature
        expire.value = route.query.expire
      }
      commit('setRequestCount', 0)
    })

    const sendVerificationCode = () => {
      isDisabledCodeButton.value = true
      setTimeout(() => {
        isDisabledCodeButton.value = false
      }, 60000)

      commit('setNotification', { isVisible: true, message: 'Please check your email', color: 'success' })

      return teamsService.sendVerificationCode({ email: props.user.email, teamName: props.team.name })
    }

    async function registerAndCreateUser () {
      if (props.hasInvite) {
        await dispatch('acceptInviteAndCreateUser', {
          firstName: props.user.firstName,
          lastName: props.user.lastName,
          signature: signature.value,
          expire: expire.value,
          email: props.user.email,
          teamId: +teamId.value,
          password: props.user.password
        })
        // window.fbq('trackCustom', 'signupTeams')
        await settingsService.update({ mode: 'Paper shape' })

        await firstTeamBook(commit, settings.value, 'Sample space', 'This is a sample space for you to discover.', 'blue')

        event('conversion', { send_to: 'AW-597169833/rbwSCLzh_OQCEKmt4JwC' })
        router.push({ name: 'TeamBooks', params: { team: props.team.name } })
      } else {
        await dispatch('registerTeam', {
          firstName: props.user.firstName,
          lastName: props.user.lastName,
          email: props.user.email,
          password: props.user.password,
          teamName: props.team.name
        })
        event('conversion', { send_to: 'AW-597169833/rbwSCLzh_OQCEKmt4JwC' })
        await settingsService.update({ mode: 'Paper shape' })
        // window.fbq('trackCustom', 'signupTeams')
        if (props.team.logo) {
          setTimeout(() => commit('loading', true), 200)
          await teamsService.upload(props.team.logo).then(res => commit('setLogo', res.data))
        }

        emit('next')
      }
    }

    async function register () {
      if (!props.hasInvite) {
        dispatch('createTeam', props.team.name)
          .then(async ({ team }) => {
            teamId.value = team.id
            if (props.team.logo) {
              setTimeout(() => commit('loading', true), 200)
              await teamsService.upload(props.team.logo).then(res => commit('setLogo', res.data))
            }
            event('conversion', { send_to: 'AW-597169833/rbwSCLzh_OQCEKmt4JwC' })
            settingsService.update({ mode: 'Paper shape' })
            // window.fbq('trackCustom', 'signupTeams')
            emit('next')
          })
      } else {
        await dispatch('acceptInvite', {
          signature: signature.value,
          expire: expire.value,
          email: props.user.email,
          teamId: +teamId.value
        })
        await settingsService.update({ mode: 'Paper shape' })
        await firstTeamBook(commit, settings.value, 'Sample space', 'This is a sample space for you to discover.', 'blue')

        event('conversion', { send_to: 'AW-597169833/rbwSCLzh_OQCEKmt4JwC' })
        // window.fbq('trackCustom', 'signupTeams')
        router.push({ name: 'TeamBooks', params: { team: props.team.name } })
      }
    }

    async function checkConfirmCode () {
      if (confirmCode.value.length === 6) {
        try {
          await teamsService.verifyCode({
            code: confirmCode.value,
            email: props.user.email,
            teamName: props.team.name
          })

          showLastStep.value = true

          if (!props.hasInvite) {
            loadingTitle.value = 'Great'
            loadingSubTitle.value = 'Let’s start with your workspace.'
            isMockupLoading.value = true

            setTimeout(() => {
              emit('next')
              showLastStep.value = false
            }, 2000)

            return
          }

          if (props.hasAccounts?.personal) {
            await register()
            showLastStep.value = false
          } else {
            await registerAndCreateUser()
            showLastStep.value = false
          }
        } catch (error) {
          showLastStep.value = false
        }
      }
    }

    function maxLengthCount () {
      if (confirmCode.value.length > 6) confirmCode.value = confirmCode.value.slice(0, 6)
    }

    return {
      // data
      confirmCode,
      isDisabledCodeButton,
      teamId,
      showLastStep,
      isMockupLoading,
      loadingTitle,
      loadingSubTitle,
      // computed
      url,
      // function
      checkConfirmCode,
      sendVerificationCode,
      maxLengthCount
    }
  }
})
</script>
